import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import {
    CardBox,
    DeliveryMethod,
    CardDetailDtoLimited,
    OriginalImage,
    BundledProductsData,
} from 'store/api-slice/types'
import {
    CardPersonDto,
    CartItemCardDetailDto,
    GetCartItemPresenter,
    GetCurrentOrderCardPresenter,
} from 'store/api-slice/hooks'

export type CardBoxField =
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'dateOfBirth'

export type CardBoxFieldExtended = CardBoxField | 'image'

interface InitialState {
    people: number
    areCardsVisible: boolean
    // TODO: ADD PHOTO
    cardBoxes: CardBox[]
    isUnlimitedPlusBeenOffered: boolean
    deliveryMethod: DeliveryMethod
    shippingPrice: number | null
    isRenewProcessVerified: boolean
    renewCardCurrentData?: GetCurrentOrderCardPresenter
    isCalendarModalOpened: boolean
}

const emptyCard: CardBox = {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    image: null,
    originalImage: null,
}

const initialState: InitialState = {
    people: 1,
    areCardsVisible: false,
    cardBoxes: [
        {
            ...emptyCard,
            cartItemId: uuidv4(),
        },
    ],
    isUnlimitedPlusBeenOffered: false,
    deliveryMethod: 'selfPickup',
    shippingPrice: null,
    isRenewProcessVerified: false,
    renewCardCurrentData: undefined,
    isCalendarModalOpened: false,
}

const unlimitedSlice = createSlice({
    name: 'card',
    initialState,
    reducers: {
        addBlankCard(state) {
            state.people += 1
            state.cardBoxes.push({ ...emptyCard, cartItemId: uuidv4() })
        },
        restoreCardsfromCart(
            state,
            action: PayloadAction<GetCartItemPresenter[]>
        ) {
            const cartCards = action.payload
            const cardBoxes: CardBox[] = cartCards.map(
                ({
                    cartItemId,
                    productId,
                    productType,
                    detail,
                    itemsBundle,
                }) => {
                    const isItemBundle = productType === 'bundle'
                    const bundledCard = itemsBundle?.filter(
                        ({ productType }) => productType === 'card'
                    )
                    const details =
                        isItemBundle && bundledCard?.length
                            ? bundledCard[0].detail
                            : detail

                    const {
                        firstName,
                        lastName,
                        dateOfBirth,
                        email,
                        phone,
                        person,
                        cardType,
                    } = details as CartItemCardDetailDto

                    const commonData = {
                        productType: 'card',
                        firstName,
                        lastName,
                        dateOfBirth,
                        email,
                        phone,
                        originalImage: null,
                        personDetail: {
                            key: person,
                        },
                        cardDetail: {
                            cardType: { value: cardType },
                        },
                    }

                    if (isItemBundle && bundledCard?.length) {
                        const { cartItemId } = bundledCard[0]
                        const bundleData: BundledProductsData = []
                        itemsBundle?.forEach(({ productId, productType }) => {
                            if (productId && productType)
                                bundleData.push({
                                    productId,
                                    productType,
                                })
                        })

                        return {
                            cartItemId,
                            productId,
                            image: cartItemId,
                            bundleData,
                            ...commonData,
                        }
                    } else
                        return {
                            cartItemId,
                            productId,
                            image: cartItemId,
                            ...commonData,
                        }
                }
            )
            state.people = cartCards.length
            state.areCardsVisible = true
            state.cardBoxes = cardBoxes
        },
        removeLastCard(state) {
            state.people -= 1
            state.cardBoxes.splice(-1)
        },
        removeAllCards(state) {
            state.cardBoxes.forEach((card) => {
                // eslint-disable-next-line compat/compat
                card.image && URL.revokeObjectURL(card.image)
            })
            state = { ...initialState }
        },
        setPersonType(
            state,
            action: PayloadAction<{
                index: number
                personDetail: CardPersonDto
            }>
        ) {
            const { index, personDetail } = action.payload

            state.cardBoxes[index] = {
                ...emptyCard,
                cartItemId: uuidv4(),
                personDetail,
            }
        },
        setCardType(
            state,
            action: PayloadAction<{
                index: number
                productId?: string
                cardDetail?: CardDetailDtoLimited
                bundleData?: BundledProductsData
            }>
        ) {
            const { index, productId, cardDetail, bundleData } = action.payload

            state.cardBoxes[index] = {
                ...state.cardBoxes[index],
                productId,
                cardDetail,
                ...(bundleData && { bundleData: [...bundleData] }),
            }
        },
        setCardDetails(
            state,
            action: PayloadAction<{
                field: CardBoxFieldExtended
                value: string
                index: number
            }>
        ) {
            const { field, value, index } = action.payload
            state.cardBoxes[index][field] = value
        },
        setImageDetails(
            state,
            action: PayloadAction<{
                value: OriginalImage | null
                index: number
            }>
        ) {
            const { value, index } = action.payload
            state.cardBoxes[index].originalImage = value ? { ...value } : null
        },
        removeCard(state, action: PayloadAction<number>) {
            const index = action.payload

            state.people -= 1
            state.cardBoxes.splice(index, 1)
        },
        setCardsVisible(state) {
            state.areCardsVisible = true
        },
        setDeliveryMethod(state, action: PayloadAction<DeliveryMethod>) {
            state.deliveryMethod = action.payload
        },
        setIsUnlimitedPlusBeenOffered(state, action: PayloadAction<boolean>) {
            state.isUnlimitedPlusBeenOffered = action.payload
        },
        setShippingPrice(state, action: PayloadAction<number>) {
            state.shippingPrice = action.payload
        },
        setIsRenewProcessVerified(state, action: PayloadAction<boolean>) {
            state.isRenewProcessVerified = action.payload
        },
        setRenewCardCurrentData(
            state,
            action: PayloadAction<GetCurrentOrderCardPresenter>
        ) {
            state.renewCardCurrentData = action.payload
        },
        setIsCalendarModalOpened(state, action: PayloadAction<boolean>) {
            state.isCalendarModalOpened = action.payload
        },
    },
})

export const {
    addBlankCard,
    restoreCardsfromCart,
    removeLastCard,
    setPersonType,
    removeCard,
    removeAllCards,
    setCardsVisible,
    setCardType,
    setCardDetails,
    setImageDetails,
    setDeliveryMethod,
    setIsUnlimitedPlusBeenOffered,
    setShippingPrice,
    setIsRenewProcessVerified,
    setRenewCardCurrentData,
    setIsCalendarModalOpened,
} = unlimitedSlice.actions
export default unlimitedSlice.reducer
